import { mat4 } from '../math/mat4'
import { quat, type Quat } from '../math/quat'
import { vec3, type Vec3 } from '../math/vec3'
import { pose as poseOps, type Pose } from '../math/pose'
import { clamp, DEG2RAD } from '../math/index'
import { snapToRay, type CameraModel } from '../camera/model'
import type { FaceFrame, PoseFrame } from '../tracking/types'
import { POSE_LM } from '../tracking/landmarks'
import type { AnchorSpec } from '../product/schema'
import { FACE_ANCHORS } from './face-anchors'

export interface NecklaceSolveResult {
  /** Anchor pose at the suprasternal notch (base of the neck), camera space, cm. Gravity-upright. */
  pose: Pose
  /** 0..1 — how much the shoulders (pose tracker) contributed (0 = head-only fallback). */
  shoulderWeight: number
  /** Shoulder line roll (rad, camera space) and estimated shoulder width (cm). */
  chest: { roll: number; width: number }
}

export interface NecklaceSolverOptions {
  /** Shoulder visibility below this → head-only fallback; linear blend up to fullVisibility. */
  minVisibility: number
  fullVisibility: number
  /** Fraction of the head's yaw the chest is assumed to follow (bodies lag the head). */
  yawFollow: number
  /** Fraction of the head's pitch applied to the chest. */
  pitchFollow: number
  /** Notch sits this fraction of the shoulder span ABOVE the shoulder midpoint (image space). */
  notchLift: number
}

export const NECKLACE_SOLVER_DEFAULTS: NecklaceSolverOptions = {
  minVisibility: 0.4,
  fullVisibility: 0.8,
  yawFollow: 0.55,
  pitchFollow: 0.25,
  notchLift: 0.16,
}

const _notch: Vec3 = [0, 0, 0]
const _snapped: Vec3 = [0, 0, 0]
const _head: Quat = [0, 0, 0, 1]
const _roll: Quat = [0, 0, 0, 1]
const _user: Quat = [0, 0, 0, 1]
const _off: Vec3 = [0, 0, 0]

/**
 * Necklace solver — face + body-pose fusion.
 *  position: canonical neck point through the head matrix (depth), screen x/y snapped to the
 *            suprasternal notch estimated from the shoulder keypoints (11/12) when visible;
 *  rotation: gravity-upright (a necklace hangs), yaw = damped head yaw, roll = shoulder line,
 *            a little pitch from the head so a bowed head tips the chain forward.
 * Degrades to a head-only solution when the shoulders are not visible.
 */
export function solveNecklace(
  frame: FaceFrame,
  camera: CameraModel,
  spec: AnchorSpec,
  poseFrame?: PoseFrame | null,
  options: Partial<NecklaceSolverOptions> = {},
  out?: NecklaceSolveResult,
): NecklaceSolveResult {
  const opts = { ...NECKLACE_SOLVER_DEFAULTS, ...options }
  const res: NecklaceSolveResult = out ?? { pose: poseOps.create(), shoulderWeight: 0, chest: { roll: 0, width: 0 } }

  // 1. head-only estimate of the notch
  mat4.transformPoint(_notch, frame.headMatrix, FACE_ANCHORS.neck.point)
  quat.fromMat4(_head, frame.headMatrix)
  const euler = quat.toEulerDeg(_head) // [pitch, yaw, roll] deg

  // 2. shoulders → notch screen position, roll, width
  let w = 0
  let roll = 0
  let width = 0
  if (poseFrame) {
    const L = POSE_LM.LEFT_SHOULDER, R = POSE_LM.RIGHT_SHOULDER
    const vis = poseFrame.visibility
    const v = vis ? Math.min(vis[L]!, vis[R]!) : 1
    w = clamp((v - opts.minVisibility) / (opts.fullVisibility - opts.minVisibility), 0, 1)
    if (w > 0 && R * 3 + 1 < poseFrame.landmarks.length) {
      const lx = poseFrame.landmarks[L * 3]!, ly = poseFrame.landmarks[L * 3 + 1]!
      const rx = poseFrame.landmarks[R * 3]!, ry = poseFrame.landmarks[R * 3 + 1]!
      const dx = (lx - rx) * camera.aspect, dy = ly - ry // height units
      const span = Math.hypot(dx, dy)
      const mx = (lx + rx) / 2, my = (ly + ry) / 2 - span * opts.notchLift
      snapToRay(_snapped, camera, _notch, mx, my)
      vec3.lerp(_notch, _notch, _snapped, w)
      // roll: shoulder line angle in camera space (x right, y up) → image y is down
      roll = Math.atan2(-dy, dx) * w
      // width in cm at the notch depth
      const depth = -_notch[2]
      width = span * 2 * depth * Math.tan((camera.fovYDeg * DEG2RAD) / 2)
    }
  }
  res.shoulderWeight = w
  res.chest.roll = roll
  res.chest.width = width

  // 3. rotation: upright, damped yaw/pitch from the head, roll from the shoulders, then user rotation
  quat.fromEulerDeg(res.pose.rotation, euler[0] * opts.pitchFollow, euler[1] * opts.yawFollow, 0)
  quat.fromAxisAngle(_roll, [0, 0, 1], roll)
  quat.multiply(res.pose.rotation, _roll, res.pose.rotation)
  quat.fromEulerDeg(_user, spec.rotation[0], spec.rotation[1], spec.rotation[2])
  quat.multiply(res.pose.rotation, res.pose.rotation, _user)
  quat.normalize(res.pose.rotation, res.pose.rotation)

  // 4. position = notch + user offset in the necklace frame
  quat.rotateVec3(_off, res.pose.rotation, spec.offset)
  vec3.add(res.pose.position, _notch, _off)
  res.pose.scale = spec.scale
  return res
}
